/**
 * queryOptions factories for non-YoY admin hooks.
 *
 * Mirrors the pattern in yoyQueryOptions.ts — standalone factories enable
 * type-safe getQueryData/invalidateQueries calls outside React components.
 *
 * Each factory threads the AbortSignal from QueryFunctionContext into
 * makeApiRequest so in-flight requests are cancelled on key changes.
 */

import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import type { RequestOptions, ApiResponse } from "@/shared/types/request";
import {
  TEST_ACCOUNTS,
  getDateRangeFromTimeframe,
  type UsageRecord,
  type FeedbackItem,
} from "../utils/adminHelpers";
import {
  getDateRangeFromTimeframe as getDashboardDateRange,
  type AttorneyExportStats,
  type ExportStats,
  type AttorneyUtilization,
  type PerformanceRow,
  type ExperimentalData,
} from "../types/dashboard";

type MakeApiRequest = (options: RequestOptions) => Promise<ApiResponse>;

// ── Usage Metrics ─────────────────────────────────────────────────────────────

export const usageMetricsOptions = (
  timeframe: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["usage-metrics", timeframe] as const,
    queryFn: async ({ signal }) => {
      const dateRange = getDateRangeFromTimeframe(timeframe);
      const queryParams = dateRange
        ? `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&excludeInternal=true`
        : "?excludeInternal=true";
      const response = await makeApiRequest({
        endpoint: `/admin/forEveryone${queryParams}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const { usageRecords = [], globalExportTimes: exportTimes = {} } =
        response.data || {};
      return {
        usageData: (usageRecords as UsageRecord[]).filter(
          (r) => !TEST_ACCOUNTS.includes(r.matter)
        ),
        globalExportTimes: exportTimes as Record<string, string | null>,
      };
    },
  });

// ── Export Stats ──────────────────────────────────────────────────────────────

export const exportStatsOptions = (
  timeframe: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["export-stats", timeframe] as const,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const { startDate, endDate } = getDashboardDateRange(timeframe);
      const response = await makeApiRequest({
        endpoint: `/admin/export-stats?startDate=${startDate}&endDate=${endDate}&excludeInternal=true`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data as ExportStats | undefined;
      if (raw) {
        return {
          attorneys: raw.attorneys.filter((a) => !TEST_ACCOUNTS.includes(a.name)),
          totals: raw.totals,
        };
      }
      return { attorneys: [], totals: null };
    },
  });

// ── Feedback ──────────────────────────────────────────────────────────────────

/**
 * Export stats for an explicit date range.
 *
 * `exportStatsOptions` derives its range from a timeframe preset; the Reports
 * page needs an arbitrary from/to, so it calls the same endpoint directly.
 */
export const exportStatsRangeOptions = (
  startDate: string,
  endDate: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["export-stats-range", startDate, endDate] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/export-stats?startDate=${startDate}&endDate=${endDate}&excludeInternal=true`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data ?? null;
      if (!raw) return null;
      return {
        ...raw,
        attorneys: raw.attorneys.filter(
          (a: AttorneyExportStats) => !TEST_ACCOUNTS.includes(a.name)
        ),
      } as ExportStats;
    },
  });

export interface MatterHoursRow {
  matter_id: string;
  matter_number: string;
  matter_description: string;
  client: string;
  status: string | null;
  hours: number;
  entries: number;
  lifetime_hours: number;
  lifetime_entries: number;
  first_activity: string | null;
  last_activity: string | null;
}

export interface MatterHoursResponse {
  startDate: string | null;
  endDate: string | null;
  matters: MatterHoursRow[];
}

/**
 * Every billable matter and the time entered against it in a date range,
 * firm-wide. Includes matters with zero hours, which is the point.
 */
export const matterHoursOptions = (
  startDate: string,
  endDate: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["matter-hours", startDate, endDate] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/matter-hours?startDate=${startDate}&endDate=${endDate}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return (response.data ?? null) as MatterHoursResponse | null;
    },
  });

export interface MatterTimekeeperRow {
  matter_id: string;
  matter_number: string;
  matter_description: string;
  client: string;
  prof_id: string;
  prof_name: string;
  prof_type: string | null;
  hours: number;
}

export interface MatterTimekeeperResponse {
  startDate: string | null;
  endDate: string | null;
  rows: MatterTimekeeperRow[];
}

/**
 * Billable hours per timekeeper per matter, firm-wide. One request; replaces
 * the per-timekeeper fan-out that could only see the caller's own reports.
 */
export const matterTimekeeperHoursOptions = (
  startDate: string,
  endDate: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["matter-timekeeper-hours", startDate, endDate] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/matter-timekeeper-hours?startDate=${startDate}&endDate=${endDate}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data as MatterTimekeeperResponse | null;
      if (!raw) return null;
      return {
        ...raw,
        rows: raw.rows.filter((r) => !TEST_ACCOUNTS.includes(r.prof_name)),
      } as MatterTimekeeperResponse;
    },
  });

export const feedbackDataOptions = (makeApiRequest: MakeApiRequest) =>
  queryOptions({
    queryKey: ["feedback"] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: "/feedback",
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return (response.data || []) as FeedbackItem[];
    },
  });

// ── Dashboard Metrics ─────────────────────────────────────────────────────────

export const dashboardMetricsOptions = (
  startDate: string | undefined,
  endDate: string | undefined,
  makeApiRequest: MakeApiRequest
) => {
  const dateParams =
    startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : "";
  return queryOptions({
    queryKey: ["dashboard-metrics", startDate ?? "default", endDate ?? "default"] as const,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/dashboard-metrics?excludeInternal=true${dateParams}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return response.data ?? null;
    },
  });
};

// ── Available Weeks ───────────────────────────────────────────────────────────

export const availableWeeksOptions = (makeApiRequest: MakeApiRequest) =>
  queryOptions({
    queryKey: ["available-weeks"] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: "/admin/billing/available-weeks",
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return response.data?.weeks ?? [];
    },
  });

// ── Timekeeper Performance ────────────────────────────────────────────────────

export const timekeeperPerformanceOptions = (
  asOfDate: string | null,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["timekeeper-perf", asOfDate ?? "latest"] as const,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const param = asOfDate
        ? `?asOfDate=${asOfDate}&excludeInternal=true`
        : "?excludeInternal=true";
      const response = await makeApiRequest({
        endpoint: `/admin/billing/rolling-performance${param}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data ?? null;
      if (!raw) return null;
      return {
        ...raw,
        rows: raw.rows.filter(
          (r: PerformanceRow) => !TEST_ACCOUNTS.includes(r.prof_name)
        ),
      };
    },
  });

// ── Experimental ─────────────────────────────────────────────────────────────

export const experimentalOptions = (makeApiRequest: MakeApiRequest) =>
  queryOptions({
    queryKey: ["experimental-all"] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: "/admin/billing/experimental",
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return (response.data ?? null) as ExperimentalData | null;
    },
  });

// ── Utilization ───────────────────────────────────────────────────────────────

export const utilizationOptions = (
  startDate: string,
  endDate: string,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["utilization", startDate, endDate] as const,
    // keepPreviousData: shows old date range data at 60% opacity while new
    // date range loads — prevents the content from disappearing on filter change
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/billing/utilization?startDate=${startDate}&endDate=${endDate}&excludeInternal=true`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const raw = response.data ?? null;
      if (!raw) return null;
      return {
        ...raw,
        attorneys: raw.attorneys.filter(
          (a: AttorneyUtilization) => !TEST_ACCOUNTS.includes(a.prof_name)
        ),
      };
    },
  });
